import React from 'react'
import {
  Alert,
  Button,
  Drawer,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography
} from 'antd'

import { APIS } from '@constants/apis'
import {
  API_SERVICES,
  createDefaultApiRoutingConfig,
  isValidLocalBaseUrl,
  loadDevApiRoutingConfig,
  saveDevApiRoutingConfig
} from '@core/utils/apiRouting'

import {
  LauncherButton,
  PanelSection,
  ServiceRow
} from './LocalApiDevPanel.styled'

const { Paragraph, Text, Title } = Typography
const { Option } = Select

const API_ENTRIES = Object.entries(APIS).flatMap(([namespace, endpoints]) =>
  Object.entries(endpoints).map(([name, endpoint]) => ({
    ...endpoint,
    key: endpoint.apiId,
    namespace,
    name
  }))
)

const LocalApiDevPanel = () => {
  const [visible, setVisible] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [draft, setDraft] = React.useState(loadDevApiRoutingConfig)

  if (process.env.NODE_ENV !== 'development') return null

  const localServiceCount = Object.values(draft.services).filter(
    ({ mode }) => mode === 'local'
  ).length
  const localEndpointCount = Object.values(draft.endpoints).filter(
    (mode) => mode === 'local'
  ).length
  const invalidServices = Object.entries(draft.services).filter(
    ([, service]) => !isValidLocalBaseUrl(service.baseUrl)
  )

  const updateService = (service, patch) => {
    setDraft((current) => ({
      ...current,
      services: {
        ...current.services,
        [service]: {
          ...current.services[service],
          ...patch
        }
      }
    }))
  }

  const updateEndpoint = (apiId, mode) => {
    setDraft((current) => {
      const endpoints = { ...current.endpoints }
      if (mode === 'inherit') {
        delete endpoints[apiId]
      } else {
        endpoints[apiId] = mode
      }
      return { ...current, endpoints }
    })
  }

  const setAllServices = (mode) => {
    const nextConfig = createDefaultApiRoutingConfig()
    Object.keys(nextConfig.services).forEach((service) => {
      nextConfig.services[service].mode = mode
      nextConfig.services[service].baseUrl =
        draft.services[service]?.baseUrl || nextConfig.services[service].baseUrl
    })
    setDraft(nextConfig)
  }

  const applyAndReload = () => {
    saveDevApiRoutingConfig(draft)
    window.location.reload()
  }

  const normalizedSearch = search.trim().toLowerCase()
  const filteredEndpoints = API_ENTRIES.filter((endpoint) => {
    if (!normalizedSearch) return true
    return [endpoint.apiId, endpoint.method || 'GET', endpoint.url]
      .join(' ')
      .toLowerCase()
      .includes(normalizedSearch)
  })

  const endpointColumns = [
    {
      title: 'Endpoint',
      dataIndex: 'apiId',
      key: 'apiId',
      render: (apiId, endpoint) => (
        <>
          <Text code>{apiId}</Text>
          <div>
            <Tag>{endpoint.method || 'GET'}</Tag>
            <Text type="secondary">{endpoint.url}</Text>
          </div>
        </>
      )
    },
    {
      title: 'Service',
      dataIndex: 'service',
      key: 'service',
      width: 100
    },
    {
      title: 'Route',
      key: 'mode',
      width: 130,
      render: (_, endpoint) => (
        <Select
          value={draft.endpoints[endpoint.apiId] || 'inherit'}
          onChange={(mode) => updateEndpoint(endpoint.apiId, mode)}
          style={{ width: 110 }}
        >
          <Option value="inherit">Inherit</Option>
          <Option value="local">Local</Option>
          <Option value="remote">Remote</Option>
        </Select>
      )
    }
  ]

  return (
    <>
      <LauncherButton
        isLocal={localServiceCount + localEndpointCount > 0}
        onClick={() => setVisible(true)}
        type="button"
      >
        API{' '}
        {localServiceCount + localEndpointCount > 0
          ? `Local ${localServiceCount}/${localEndpointCount}`
          : 'Remote'}
      </LauncherButton>

      <Drawer
        title="Local API Router"
        placement="right"
        width={760}
        visible={visible}
        onClose={() => setVisible(false)}
      >
        <Alert
          type="info"
          showIcon
          message="Development only"
          description="Endpoint overrides take priority over service routing. Apply reloads the page so startup requests use the same configuration."
        />

        <PanelSection>
          <Title level={4}>Services</Title>
          {Object.entries(API_SERVICES).map(([service, definition]) => (
            <ServiceRow key={service}>
              <Text strong>
                {definition.label} <Text type="secondary">({service})</Text>
              </Text>
              <Switch
                checked={draft.services[service].mode === 'local'}
                checkedChildren="Local"
                unCheckedChildren="Remote"
                onChange={(checked) =>
                  updateService(service, {
                    mode: checked ? 'local' : 'remote'
                  })
                }
              />
              <Input
                style={{
                  borderColor: isValidLocalBaseUrl(
                    draft.services[service].baseUrl
                  )
                    ? undefined
                    : '#ff4d4f'
                }}
                value={draft.services[service].baseUrl}
                onChange={(event) =>
                  updateService(service, { baseUrl: event.target.value })
                }
              />
            </ServiceRow>
          ))}
          <Space>
            <Button onClick={() => setAllServices('local')}>All local</Button>
            <Button onClick={() => setAllServices('remote')}>All remote</Button>
            <Button onClick={() => setDraft(createDefaultApiRoutingConfig())}>
              Reset defaults
            </Button>
          </Space>
        </PanelSection>

        <PanelSection>
          <Title level={4}>Endpoint overrides</Title>
          <Paragraph type="secondary">
            Use Inherit to follow the selected service mode.
          </Paragraph>
          <Input.Search
            allowClear
            placeholder="Search API id, method, or path"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ marginBottom: 12 }}
          />
          <Table
            columns={endpointColumns}
            dataSource={filteredEndpoints}
            pagination={{ pageSize: 8, showSizeChanger: false }}
            size="small"
          />
        </PanelSection>

        {invalidServices.length > 0 && (
          <Alert
            type="error"
            showIcon
            message="Use an HTTP/HTTPS loopback URL such as localhost or 127.0.0.1."
            style={{ marginBottom: 16 }}
          />
        )}

        <Button
          block
          type="primary"
          disabled={invalidServices.length > 0}
          onClick={applyAndReload}
        >
          Apply &amp; Reload
        </Button>
      </Drawer>
    </>
  )
}

export default LocalApiDevPanel
